import React from 'react';
import compose from 'helpers/compose';
import { observer } from 'mobx-react';

import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import IconButton from '@material-ui/core/IconButton';
import Close from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { EdwardColors } from 'enums';
import Button from '@material-ui/core/Button';
import { borderRadiusBox } from 'components/AppTheme/index';
import Info from 'components/Tooltip/Info';

import { UserStoreKC, ViewKeys } from 'stores/userStoreKC';
import '../AppTheme/index';

export enum MessageVariant {
  SUCCESS = 'SUCCESS',
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  ACTION = 'ACTION',
  LIGHT = 'LIGHT'
}

interface Props {
  id: string;
  title?: string;
  message?: string;
  content?: React.ReactElement;
  variant: MessageVariant;
  userStore?: UserStoreKC;
  stack?: boolean;
  permanent?: boolean;
  action?: Function;
  actionText?: string;
  actionIcon: React.ReactElement | false;
  responsive?: boolean;
  reenableIdOnClose?: string;
  tooltip?: string;
  style?: React.CSSProperties;
}

const useAlertStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props: { responsive?: boolean; action?: boolean }) => ({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, props.action ? '200px' : 8, 2, 1),
      margin: theme.spacing(1, 0),
      borderRadius: borderRadiusBox,

      '&.SUCCESS': {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
        '& svg': {
          color: theme.palette.common.white,
        },
      },
      '&.LIGHT': {
        backgroundColor: EdwardColors.LIGHT,
        color: theme.palette.common.black,
        '& svg': {
          color: '#666',
        },
      },
      '&.INFO': {
        backgroundColor: EdwardColors.DARK,
        color: theme.palette.common.black,
        '& svg': {
          color: theme.palette.common.black,
        },
      },
      '&.WARNING': {
        backgroundColor: EdwardColors.SECONDARY,
        color: theme.palette.common.white,
        '& svg': {
          color: theme.palette.common.white,
        },
      },
      '&.ERROR': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        '& svg': {
          color: theme.palette.common.white,
        },
      },
      '&.ACTION': {
        position: 'relative',
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        '& svg': {
          color: theme.palette.common.white,
        },
      },
      '& > svg': {
        margin: theme.spacing(0, 1, 0, 2),
      },
      '& strong': {
        marginRight: theme.spacing(1),
      },
      '& button': {
        padding: props.action ? undefined : theme.spacing(1),
        position: 'absolute',
        right: theme.spacing(5),
        top: 'auto',
      },
      '& .MuiButton-label svg': {
        marginLeft: theme.spacing(1),
      },
      [theme.breakpoints.down('xs')]: props.responsive
        ? {
            background: 'transparent !important',
            padding: 0,
            width: 'auto',
            display: 'inline-flex',
            '& > .MuiSvgIcon-root, & > span': { display: 'none' },
            '& button': {
              position: 'relative',
              right: 'auto',
            },
          }
        : props.action
        ? {
            position: 'relative',
            paddingRight: theme.spacing(2),
            paddingBottom: theme.spacing(7),
            '& > *': { display: 'inline-flex' },
            '& > button' : {
              position: 'absolute',
              right: theme.spacing(2),
              bottom: theme.spacing(2),
            }
          }
        : undefined,
    }),
    stack: {
      borderBottom: 'solid 1px #000',
      padding: '0 !important',
      margin: '0 !important',
      borderRadius: '0 !important',
      '& button': {
        position: 'relative !important',
        right: 'auto !important',
      },
    },
  }),
);

const Message: React.FC<Props> = ({
  title,
  message,
  content,
  variant,
  id,
  userStore,
  stack,
  permanent,
  action,
  actionText,
  actionIcon = <Close />,
  responsive,
  reenableIdOnClose,
  tooltip,
  style
}) => {
  const classes = useAlertStyles({ responsive, action: !!action });
  return !userStore || !userStore.inViewSetting(ViewKeys.HIDE_MESSAGES, id) ? (
    <div className={`${classes.root} ${variant} ${stack ? classes.stack : ''}`} key={`msg_${id}`} id={`msg_${id}`} style={style}>
      {variant === MessageVariant.SUCCESS && <CheckIcon />}
      {(variant === MessageVariant.INFO || variant === MessageVariant.ACTION || variant === MessageVariant.LIGHT) && <InfoIcon />}
      {variant === MessageVariant.WARNING && <WarningIcon />}
      {variant === MessageVariant.ERROR && <ErrorOutlineIcon />}
      <strong>{title && `${title}${message ? ': ' : ''}`}</strong>
      { message && <span>{message}{tooltip && <Info content={tooltip} heading />}</span>}
      { content }
      {userStore && !permanent && (
        <IconButton size="medium" onClick={() => {
            userStore.addToViewSetting(ViewKeys.HIDE_MESSAGES, id);
            reenableIdOnClose && userStore.removeFromViewSetting(ViewKeys.HIDE_MESSAGES, reenableIdOnClose);
          }}>
          <Close fontSize="inherit" />
        </IconButton>
      )}
      {action && (
        <Button
          variant={'contained'}
          color={variant === MessageVariant.ACTION ? 'default' : 'primary'}
          onClick={() => action()}
        >
          {actionText}
          {actionIcon}
        </Button>
      )}
    </div>
  ) : null;
};

export default compose(observer)(Message);
